import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Link2, Plus, Trash2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/Pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperSeededPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const [formData, setFormData] = useState({
    original_part_number: '',
    super_seeded_part_number: '',
    reason: '',
    price_adjustment_percent: 0
  });
  const { hasRole } = useAuth();

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API}/super-seeded`);
      setRecords(response.data);
    } catch (error) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/super-seeded`, formData);
      toast.success('Super seeded relationship created');
      setDialogOpen(false);
      setFormData({
        original_part_number: '',
        super_seeded_part_number: '',
        reason: '',
        price_adjustment_percent: 0
      });
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create relationship');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this relationship?')) return;
    
    try {
      await axios.delete(`${API}/super-seeded/${id}`);
      toast.success('Relationship deleted');
      fetchRecords();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(records.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = records.slice(startIndex, endIndex);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-8 space-y-8" data-testid="super-seeded-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-zinc-900 dark:text-white tracking-tight">Super Seeded Parts</h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">Manage relationships between superseded parts</p>
          </div>
          {hasRole('admin') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#C3002F] hover:bg-[#9f0026]" data-testid="add-super-seeded-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Relationship
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-zinc-900 dark:text-white">Create Super Seeded Relationship</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <Label className="text-zinc-700 dark:text-zinc-300">Original Part Number</Label>
                    <Input
                      value={formData.original_part_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, original_part_number: e.target.value }))}
                      placeholder="e.g., NSN-FLT-0001"
                      className="bg-zinc-50 dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800 mt-1 font-mono"
                      required
                      data-testid="original-part-input"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-700 dark:text-zinc-300">Super Seeded Part Number</Label>
                    <Input
                      value={formData.super_seeded_part_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, super_seeded_part_number: e.target.value }))}
                      placeholder="e.g., NSN-FLT-0005"
                      className="bg-zinc-50 dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800 mt-1 font-mono"
                      required
                      data-testid="super-seeded-part-input"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-700 dark:text-zinc-300">Reason</Label>
                    <Input
                      value={formData.reason}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="e.g., Updated design for better performance"
                      className="bg-zinc-50 dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800 mt-1"
                      required
                      data-testid="reason-input"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-700 dark:text-zinc-300">Price Adjustment (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.price_adjustment_percent}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_adjustment_percent: parseFloat(e.target.value) || 0 }))}
                      className="bg-zinc-50 dark:bg-zinc-900 border-zinc-300 dark:border-zinc-800 mt-1 font-mono"
                      data-testid="adjustment-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Recommended price adjustment for the super seeded part
                    </p>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-300 dark:border-zinc-700">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#C3002F] hover:bg-[#9f0026]" data-testid="save-relationship-btn">
                      Create Relationship
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Info Card */}
        <div className="p-4 bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 rounded-lg text-sm">
          <p className="text-cyan-700 dark:text-cyan-400 font-medium mb-1">What are Super Seeded Parts?</p>
          <p className="text-zinc-600 dark:text-zinc-400">
            Super seeded parts are updated versions of existing parts with minor modifications. 
            When a part has a super seeded relationship, the system will flag this during cost card creation 
            and suggest price adjustments based on the original part&apos;s pricing.
          </p>
        </div>

        {/* Records Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedRecords.map((record) => (
            <div
              key={record.id}
              className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 rounded-lg hover:border-zinc-300 dark:hover:border-zinc-700 transition-all duration-200 group"
              data-testid={`super-seeded-card-${record.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-100 dark:bg-cyan-500/20 rounded">
                    <Link2 className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Relationship</p>
                  </div>
                </div>
                {hasRole('admin') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(record.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
                    data-testid={`delete-relationship-${record.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1 p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded">
                  <p className="text-xs text-zinc-500">Original</p>
                  <p className="font-mono text-zinc-900 dark:text-white">{record.original_part_number}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-cyan-500 dark:text-cyan-400 flex-shrink-0" />
                <div className="flex-1 p-3 bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 rounded">
                  <p className="text-xs text-cyan-600 dark:text-cyan-400">Super Seeded</p>
                  <p className="font-mono text-zinc-900 dark:text-white">{record.super_seeded_part_number}</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div>
                  <p className="text-xs text-zinc-500">Reason</p>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300">{record.reason}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-zinc-500">Price Adjustment</p>
                  <span className={`text-sm font-mono ${record.price_adjustment_percent >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {record.price_adjustment_percent >= 0 ? '+' : ''}{record.price_adjustment_percent}%
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-800 text-xs text-zinc-500">
                Created: {new Date(record.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>

        {records.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Link2 className="w-16 h-16 text-zinc-300 dark:text-zinc-700 mb-4" />
            <h3 className="text-xl font-semibold text-zinc-600 dark:text-zinc-400">No Super Seeded Relationships</h3>
            <p className="text-zinc-500 mt-2">Create relationships to link superseded parts</p>
          </div>
        )}

        {/* Pagination */}
        {records.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={records.length}
            itemsPerPage={itemsPerPage}
            className="mt-6"
          />
        )}
      </div>
    </MainLayout>
  );
}
